// src/services/permissions/dataroomPermissionService.js
// Dataroom-specific permission service
/* eslint-disable no-unused-vars */

import { supabase } from '../../config';
import { ROLES } from '../constants/roleConstants';
import { checkSuperAdmin } from './adminPermissionService';

/**
 * Get user's role for a specific dataroom project
 * @param {string} userId - User ID
 * @param {string} projectId - Project ID
 * @returns {Promise<string|null>} User's role or null
 */
export const getDataroomRole = async (userId, projectId) => {
    try {
        const isSuperAdmin = await checkSuperAdmin(userId);
        if (isSuperAdmin) return ROLES.SUPER_ADMIN;

        const { data, error } = await supabase
            .from('dataroom_users')
            .select('role')
            .eq('user_id', userId)
            .eq('project_id', projectId)
            .single();
        
        if (error) {
            if (error.code === 'PGRST116') return null;
            throw error;
        }
        
        return data?.role || null;
    } catch (error) {
        console.error('Error getting dataroom role:', error);
        return null;
    }
};

/**
 * Get all datarooms for a user
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Array of dataroom projects
 */
export const getUserDatarooms = async (userId) => {
    try {
        const isSuperAdmin = await checkSuperAdmin(userId);
        
        if (isSuperAdmin) {
            const { data, error } = await supabase
                .from('dataroom_projects')
                .select('*')
                .order('created_at', { ascending: false });
            
            if (error) throw error;
            return data || [];
        }

        const { data, error } = await supabase
            .from('dataroom_users')
            .select(`
                *,
                project:dataroom_projects(*)
            `)
            .eq('user_id', userId);
        
        if (error) throw error;
        return data?.map(item => item.project).filter(Boolean) || [];
    } catch (error) {
        console.error('Error getting user datarooms:', error);
        return [];
    }
};

/**
 * Assign user to dataroom
 * @param {string} userId - User ID
 * @param {string} projectId - Project ID
 * @param {string} role - Role to assign
 * @param {string} invitedBy - User ID who invited
 * @returns {Promise<Object>} Result object
 */
export const assignDataroomRole = async (userId, projectId, role, invitedBy = null) => {
    try {
        const { data: existingUser } = await supabase
            .from('dataroom_users')
            .select('id')
            .eq('user_id', userId)
            .eq('project_id', projectId)
            .single();

        if (existingUser) {
            // Update existing role
            const { data, error } = await supabase
                .from('dataroom_users')
                .update({ role, status: 'active' })
                .eq('user_id', userId)
                .eq('project_id', projectId);
            
            if (error) throw error;
            return { success: true, data };
        } else {
            // Create new role assignment
            const { data, error } = await supabase
                .from('dataroom_users')
                .insert({
                    user_id: userId,
                    project_id: projectId,
                    role,
                    invited_by: invitedBy,
                    status: 'active'
                });
            
            if (error) throw error;
            return { success: true, data };
        }
    } catch (error) {
        console.error('Error assigning dataroom role:', error);
        return { success: false, error: error.message };
    }
};

/**
 * Get all users for a dataroom project
 * @param {string} projectId - Project ID
 * @returns {Promise<Array>} Array of users with roles
 */
export const getDataroomUsers = async (projectId) => {
    try {
        const { data, error } = await supabase
            .from('dataroom_users')
            .select(`
                *,
                user:user_profiles(*)
            `)
            .eq('project_id', projectId)
            .order('invited_at', { ascending: false });
        
        if (error) throw error;
        return data || [];
    } catch (error) {
        console.error('Error getting dataroom users:', error);
        return [];
    }
};

/**
 * Remove user from dataroom
 * @param {string} userId - User ID
 * @param {string} projectId - Project ID
 * @returns {Promise<Object>} Result object
 */
export const removeDataroomUser = async (userId, projectId) => {
    try {
        const { error } = await supabase
            .from('dataroom_users')
            .delete()
            .eq('user_id', userId)
            .eq('project_id', projectId);
        
        if (error) throw error;
        return { success: true };
    } catch (error) {
        console.error('Error removing dataroom user:', error);
        return { success: false, error: error.message };
    }
};
